// src/components/UpgradeModal.jsx
// Subscription upgrade modal — GH₵300 / GH₵550 per user per month

import React, { useState } from 'react';
import { X, Check, Zap, Star, Building2, Users, Loader, Phone } from 'lucide-react';
import { usePlan } from '../contexts/PlanContext';
import { useFirm } from '../contexts/FirmContext';
import { PAID_PLANS } from '../lib/plans';
import { updateFirm } from '../lib/db';
import { useActivityLogger } from '../lib/useActivityLogger';

// ── Per-plan feature bullets ─────────────────────────────────────────────────
const PLAN_SECTIONS = [
  {
    key:   'basic',
    icon:  Zap,
    color: 'blue',
    includes: [
      'Unlimited matters & case files',
      'Client & contact management',
      'Tasks, calendar & deadlines',
      'Time tracking & timesheets',
      'Invoicing & receipts',
      'Document management (20 GB)',
      'Expense tracking',
      'Dashboard & activity feed',
      'Email support',
    ],
  },
  {
    key:   'pro',
    icon:  Star,
    color: 'indigo',
    includes: [
      'Everything in Basic',
      'Client portal (secure client access)',
      'Trust / client account management',
      'Advanced reports & analytics',
      'Role-based access control',
      'Conflict of interest checker',
      'Online intake forms',
      'Employee & HR management',
      'Custom billing rates per matter',
      'Recurring invoices & payment links',
      'Document storage (200 GB)',
      'Priority support',
    ],
  },
  {
    key:   'enterprise',
    icon:  Building2,
    color: 'purple',
    includes: [
      'Everything in Pro',
      'AI case search & research',
      'AI document summarisation',
      'AI contract review',
      'Smart deadline alerts (AI)',
      'AI billing suggestions',
      'Unlimited document storage',
      'Dedicated account manager',
      'Custom integrations & API access',
    ],
  },
];

const COLOR_MAP = {
  blue:   { ring: 'ring-blue-500',   btn: 'bg-blue-600 hover:bg-blue-700',     badge: 'bg-blue-100 text-blue-700',     header: 'bg-blue-50 border-blue-100',     check: 'text-blue-500'   },
  indigo: { ring: 'ring-indigo-500', btn: 'bg-indigo-600 hover:bg-indigo-700', badge: 'bg-indigo-100 text-indigo-700', header: 'bg-indigo-50 border-indigo-100', check: 'text-indigo-500' },
  purple: { ring: 'ring-purple-500', btn: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-100 text-purple-700', header: 'bg-purple-50 border-purple-100', check: 'text-purple-500' },
};

export default function UpgradeModal({ highlightPlan, onClose }) {
  const { planKey, userCount } = usePlan();
  const { firmId, refresh }   = useFirm();
  const log = useActivityLogger();
  const [selected, setSelected] = useState(highlightPlan ?? 'pro');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  const handleUpgrade = async () => {
    // All paid-tier upgrades — including Basic→Pro and Pro→Basic — go
    // through support for now. Previously this did a direct
    // `updateFirm({ subscription_plan, subscription_status })` call,
    // but:
    //   1. Migration 155's trigger blocks user-side writes (42501)
    //   2. The write wouldn't actually re-charge the customer via
    //      Paystack — it would just claim the firm was on a new plan
    //      without changing the actual billing
    //
    // The proper flow is: customer pays through Paystack inline.js
    // (like SubscriptionSuspendedPage does for post-trial activation),
    // webhook fires subscription.create on the new plan, firm gets
    // the new caps. That's a separate engineering task (the in-app
    // active-tier-switch UI isn't built yet). Until then, route all
    // upgrades to support so the human can update the customer's
    // Paystack subscription manually.
    const subject = encodeURIComponent(
      `Upgrade request: ${planKey || 'current'} → ${selected}`
    );
    const body = encodeURIComponent(
      `Hi DocketFlow team,\n\n`
      + `Please upgrade my firm from ${planKey || 'current plan'} to ${selectedMeta?.name || selected}.\n\n`
      + `Firm ID: ${firmId}\n\n`
      + `Thanks!`
    );
    log('subscription', firmId, 'upgrade-requested', `User requested upgrade to ${selectedMeta?.name || selected}`);
    window.open(`mailto:hello@docketflowapp.com?subject=${subject}&body=${body}`, '_blank');
    onClose();
  };

  const selectedMeta = PAID_PLANS.find(p => p.key === selected);

  const priceLabel = (meta) => {
    if (meta.priceGhs == null) return 'Custom';
    return `GH₵${meta.priceGhs.toLocaleString()}`;
  };

  const monthlyTotal = (meta) => {
    if (meta.priceGhs == null) return null;
    return (meta.priceGhs * userCount).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Choose your plan</h2>
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
              <Users size={14} />
              {userCount} active user{userCount !== 1 ? 's' : ''} · billed per user / month in GHS
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition">
            <X size={20} />
          </button>
        </div>

        {/* Plan cards */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLAN_SECTIONS.map((plan) => {
            const meta       = PAID_PLANS.find(p => p.key === plan.key);
            const c          = COLOR_MAP[plan.color];
            const Icon       = plan.icon;
            const isCurrent  = planKey === plan.key || (planKey === 'ai' && plan.key === 'enterprise');
            const isSelected = selected === plan.key;
            const total      = monthlyTotal(meta);
            const isEnterprise = plan.key === 'enterprise';

            return (
              <div
                key={plan.key}
                onClick={() => setSelected(plan.key)}
                className={`relative rounded-xl border-2 cursor-pointer transition-all
                  ${isSelected
                    ? `ring-2 ${c.ring} border-transparent shadow-md`
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                {/* Popular badge */}
                {meta?.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Card header */}
                <div className={`px-5 pt-6 pb-4 rounded-t-xl border-b ${c.header}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Icon size={18} className={c.check} />
                      <span className="font-bold text-gray-900">{meta?.name}</span>
                    </div>
                    {isCurrent && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.badge}`}>
                        Current
                      </span>
                    )}
                  </div>
                  <div className="mb-0.5">
                    <span className="text-3xl font-extrabold text-gray-900">{priceLabel(meta)}</span>
                    {!isEnterprise && <span className="text-gray-500 text-sm"> /user/mo</span>}
                  </div>
                  {total && (
                    <p className="text-xs text-gray-400">
                      ~GH₵{total}/mo for {userCount} user{userCount !== 1 ? 's' : ''}
                    </p>
                  )}
                  {isEnterprise && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                      <Phone size={11} /> Contact us for pricing
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="px-5 py-4 space-y-2">
                  {plan.includes.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check size={14} className={`mt-0.5 flex-shrink-0 ${c.check}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col items-center gap-3 border-t border-gray-100 pt-5">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 w-full text-center">
              {error}
            </p>
          )}

          <button
            onClick={handleUpgrade}
            disabled={saving || (selected === planKey && selected !== 'enterprise')}
            className="w-full max-w-xs py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 transition flex items-center justify-center gap-2"
          >
            {saving && <Loader size={16} className="animate-spin" />}
            {selected === 'enterprise'
              ? 'Contact us'
              : selected === planKey
              ? "You're already on this plan"
              : `Upgrade to ${selectedMeta?.name}`}
          </button>

          <p className="text-xs text-gray-400 text-center max-w-sm">
            Prices in Ghana Cedis (GHS), billed monthly per active user.
            Upgrade takes effect immediately. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
